
import React from "react";
import { useLanguage } from "@/context/LanguageContext";
import { useMedia } from "./context";
import { MediaItemData } from "./types/mediaTypes";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { useToast } from "@/hooks/use-toast";
import { v4 as uuidv4 } from "uuid";

// Import our components
import ImageUploader from "./components/ImageUploader";
import VideoUrlField from "./components/VideoUrlField";
import SdgSelector from "./components/SdgSelector";

interface MediaItemFormProps {
  item: MediaItemData;
  onClose: () => void;
}

const MediaItemForm: React.FC<MediaItemFormProps> = ({ item, onClose }) => {
  const { language } = useLanguage();
  const { updateMediaItem } = useMedia();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  const form = useForm({
    defaultValues: {
      titleEn: item.title.en,
      titleAr: item.title.ar,
      image: item.image,
      videoUrl: item.videoUrl || "",
      sdgId: item.sdgId.toString(),
    }
  });

  const onSubmit = async (data: any) => {
    try {
      setIsSubmitting(true);
      
      // Create updated item
      const updatedItem: MediaItemData = {
        ...item,
        id: item.id === "new" || item.id.startsWith("new-") ? uuidv4() : item.id,
        title: {
          en: data.titleEn,
          ar: data.titleAr
        },
        image: data.image,
        videoUrl: item.type === "video" ? data.videoUrl : undefined,
        sdgId: parseInt(data.sdgId, 10),
      };
      
      // Update the UI with the new item
      updateMediaItem(updatedItem);
      
      toast({
        title: language === "en" ? "Changes saved" : "تم حفظ التغييرات",
        description: language === "en" ? "Media item updated successfully" : "تم تحديث عنصر الوسائط بنجاح",
      });
      
      onClose();
    } catch (error) {
      console.error("Error saving media item:", error);
      toast({
        title: language === "en" ? "Save failed" : "فشل الحفظ",
        description: language === "en" ? "An unexpected error occurred" : "حدث خطأ غير متوقع",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-4">
        <FormField
          control={form.control}
          name="titleEn"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === "en" ? "English Title" : "العنوان بالإنجليزية"}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={language === "en" ? "Enter title in English" : "أدخل العنوان بالإنجليزية"} 
                  {...field} 
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="titleAr"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === "en" ? "Arabic Title" : "العنوان بالعربية"}</FormLabel>
              <FormControl>
                <Input 
                  placeholder={language === "en" ? "Enter title in Arabic" : "أدخل العنوان بالعربية"} 
                  {...field} 
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="image"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{language === "en" ? "Image" : "الصورة"}</FormLabel>
              <FormControl>
                <ImageUploader 
                  imageUrl={field.value} 
                  onImageChange={(url) => form.setValue("image", url)} 
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {item.type === "video" && (
          <VideoUrlField form={form} disabled={isSubmitting} />
        )}

        <SdgSelector form={form} disabled={isSubmitting} />

        <div className="flex justify-end space-x-2 pt-4">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={isSubmitting}
          >
            {language === "en" ? "Cancel" : "إلغاء"}
          </Button>
          <Button 
            type="submit"
            className="bg-palsustain-green hover:bg-palsustain-green/90"
            disabled={isSubmitting}
          >
            {language === "en" ? "Save Changes" : "حفظ التغييرات"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default MediaItemForm;
